import { getParticipantId } from "../../../hooks/participants/helpers";
import { extractIdFromIri } from "../../../utils/extractFromIri";
import { getTextByLang } from "../../../utils/formUtils";
import { iriLast } from "../../../utils/iriLast";

const CONTRACT_STATUS_KEY_ALIASES = {
  COMPLETO: "COMPLETED",
  COMPLETADO: "COMPLETED",
};

export function parseWorkerBody(res) {
  if (typeof res?.body === "string") {
    try {
      return JSON.parse(res.body);
    } catch {
      return res.body;
    }
  }

  return res?.data ?? res ?? null;
}

export function listFromResponse(res) {
  const body = parseWorkerBody(res);

  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.content)) return body.content;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body?.data)) return body.data;
  if (body && typeof body === "object") return [body];

  return [];
}

export function normalizeContract(item) {
  const assetId =
    item?.assetId || item?.asset_id || item?.assedId || item?.assed_id || null;

  return {
    ...item,
    id:
      item?.["@id"] ||
      item?.id ||
      item?.contractAgreementId ||
      item?.contract_agreement_id ||
      item?.contractId ||
      crypto.randomUUID(),
    assetId,
    contractAgreementId:
      item?.["@id"] ||
      item?.contractAgreementId ||
      item?.contract_agreement_id ||
      item?.id ||
      null,
    name: item?.resource_title || item?.title || item?.name || assetId || "-",
    provider: item?.providerId || item?.provider_id || item?.provider || "-",
    consumer: item?.consumerId || item?.consumer_id || item?.consumer || "-",
    status: item?.status || item?.state || "-",
    contractSigningDate:
      item?.contractSigningDate ?? item?.contract_signing_date ?? null,
  };
}

export function distributionIdFromItem(item) {
  return item?.identifier || iriLast(item?.id) || item?.id || null;
}

export function formatUnixDate(value) {
  if (value === null || typeof value === "undefined" || value === "") {
    return "-";
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);

  const millis = numeric < 10_000_000_000 ? numeric * 1000 : numeric;
  return new Date(millis).toLocaleString();
}

export function normalizeContractStatus(status) {
  const normalized = String(status ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

  return CONTRACT_STATUS_KEY_ALIASES[normalized] || normalized;
}

export function getContractStatusLabel(status, t) {
  if (status === null || typeof status === "undefined" || status === "") {
    return "-";
  }

  const normalizedStatus = normalizeContractStatus(status);
  const key = `privateArea.actionLog.states.${normalizedStatus}`;
  const resolved = t(key);

  return resolved === key ? normalizedStatus : resolved;
}

function getPossibleLookupKeys(value) {
  const normalized = String(value ?? "").trim();
  if (!normalized || normalized === "-") return [];

  return [...new Set([normalized, extractIdFromIri(normalized), iriLast(normalized)])]
    .filter(Boolean);
}

export function buildParticipantsById(participants = []) {
  return (Array.isArray(participants) ? participants : []).reduce(
    (acc, participant) => {
      const participantId = getParticipantId(participant);

      getPossibleLookupKeys(participantId).forEach((key) => {
        acc[key] = participant;
      });

      return acc;
    },
    {},
  );
}

export function getContractParticipantLabel(
  participantId,
  participantsById,
  lang,
) {
  const fallback = String(participantId ?? "").trim() || "-";

  for (const key of getPossibleLookupKeys(participantId)) {
    const participant = participantsById[key];
    const displayName =
      (participant?.preferredLabel
        ? getTextByLang(participant.preferredLabel, lang)
        : "") ||
      (participant?.identifier?.[0]?.schemeName
        ? getTextByLang(participant.identifier[0].schemeName, lang)
        : "") ||
      participant?.identifier?.[0]?.notation ||
      "";

    if (displayName) return displayName;
  }

  return fallback;
}
